import { KeyboardKeyHold } from "hold-event";
import { MathUtils } from "three";

import { ControllerStrategy } from "../strategy";
import { Context } from "../../../context";
import { SpinControllerStrategy } from "../tracking";
import { CameraMode, SmoothTimes } from "../../controller";

const TIMEOUT_DURATION: number = 2 * 1000;
const ROTATE_COEFFICIENT: number = 0.1 * MathUtils.DEG2RAD;
const MOVE_COEFFICIENT: number = 0.01;

/**
 *  By default KeyboardKeyHold fires a 'holding' event every
 *  animation frame, we change that to every x milliseconds.
 *  Higher timeout => fewer holding events per second.
 */
const HOLDING_TIMEOUT: number = 10;

/**
 * Keyboard control defaults. Can be subclassed to alter behaviour (reimplement
 * moveRight() for example).
 */
export class KeyboardHoldControllerStrategy extends ControllerStrategy {
  // TODO: work out how to type eventListeners
  private keys: { [key: string]: [any, KeyboardKeyHold] } = {};
  private cachedSmoothTime = SmoothTimes.DEFAULT;
  private holdingCount: number = 0;
  private timeout: any = undefined;

  private KEYCODE = {
    W: 87,
    A: 65,
    S: 83,
    D: 68,
    LEFT: 37,
    UP: 38,
    RIGHT: 39,
    DOWN: 40,
  };

  init(context: Context): ControllerStrategy {
    super.init(context);

    this.keys.left = [
      (event: any) =>
        event && this.moveRight(-MOVE_COEFFICIENT * event.deltaTime),
      new KeyboardKeyHold(this.KEYCODE.LEFT, HOLDING_TIMEOUT),
    ];
    this.keys.right = [
      (event: any) =>
        event && this.moveRight(MOVE_COEFFICIENT * event.deltaTime),
      new KeyboardKeyHold(this.KEYCODE.RIGHT, HOLDING_TIMEOUT),
    ];
    this.keys.up = [
      (event: any) => event && this.moveUp(-MOVE_COEFFICIENT * event.deltaTime),
      new KeyboardKeyHold(this.KEYCODE.UP, HOLDING_TIMEOUT),
    ];
    this.keys.down = [
      (event: any) => event && this.moveUp(MOVE_COEFFICIENT * event.deltaTime),
      new KeyboardKeyHold(this.KEYCODE.DOWN, HOLDING_TIMEOUT),
    ];
    this.keys.d = [
      (event: any) =>
        event && this.rotateSide(-ROTATE_COEFFICIENT * event.deltaTime),
      new KeyboardKeyHold(this.KEYCODE.D, HOLDING_TIMEOUT),
    ];
    this.keys.a = [
      (event: any) =>
        event && this.rotateSide(ROTATE_COEFFICIENT * event.deltaTime),
      new KeyboardKeyHold(this.KEYCODE.A, HOLDING_TIMEOUT),
    ];
    this.keys.s = [
      (event: any) =>
        event && this.rotateUp(-0.25 * ROTATE_COEFFICIENT * event.deltaTime),
      new KeyboardKeyHold(this.KEYCODE.S, HOLDING_TIMEOUT),
    ];
    this.keys.w = [
      (event: any) =>
        event && this.rotateUp(0.5 * ROTATE_COEFFICIENT * event.deltaTime),
      new KeyboardKeyHold(this.KEYCODE.W, HOLDING_TIMEOUT),
    ];

    for (const name in this.keys) {
      this.keys[name][1].addEventListener("holdStart", () =>
        this.startSpinController()
      );
      this.keys[name][1].addEventListener("holding", this.keys[name][0]);
      this.keys[name][1].addEventListener("holdEnd", () =>
        this.stopSpinController()
      );
    }

    return this;
  }

  startSpinController() {
    clearTimeout(this.timeout);
    if (this.holdingCount == 0) {
      if (this.registered) {
        switch (this.registered.mode) {
          case CameraMode.MAP:
          case CameraMode.ORBIT:
          case CameraMode.FREE:
          case CameraMode.STRIKEZONE:
          case CameraMode.LOOK_AT_PLAYER:
          case CameraMode.LOOK_AT_SIMPLE:
          case CameraMode.LOOK_AT_BALL:
            break;
          default:
            this.registered.addAuxStrategy(SpinControllerStrategy);
        }
        this.registered.state.following = false;

        if (this.registered.controls) {
          this.cachedSmoothTime = this.registered.controls.smoothTime;
          this.registered.controls.smoothTime = SmoothTimes.FAST;
        }
      }
    }
    this.holdingCount++;
  }

  stopSpinController() {
    this.holdingCount--;

    this.timeout = setTimeout(() => {
      if (this.registered && this.holdingCount == 0) {
        this.registered.removeAuxStrategy(SpinControllerStrategy);
        this.registered.state.following = true;
        const controls = this.registered?.controls;
        if (controls) {
          controls.smoothTime = this.cachedSmoothTime;
        }
      }
    }, TIMEOUT_DURATION);
  }

  moveRight(delta: number) {
    const controls = this.registered?.controls;
    if (!controls) {
      return;
    }

    controls.truck(delta, 0, false);
  }

  moveUp(delta: number) {
    const controls = this.registered?.controls;
    if (!controls) {
      return;
    }

    controls.truck(0, delta, false);
  }

  rotateSide(delta: number) {
    const controls = this.registered?.controls;
    if (!controls) {
      return;
    }

    controls.rotate(delta, 0, true);
  }

  rotateUp(delta: number) {
    const controls = this.registered?.controls;
    if (!controls) {
      return;
    }

    controls.rotate(0, delta, true);
  }

  teardown() {
    super.teardown();

    for (const name in this.keys) {
      this.keys[name][1].removeEventListener("holdStart", () =>
        this.startSpinController()
      );
      this.keys[name][1].removeEventListener("holding", this.keys[name][0]);
      this.keys[name][1].removeEventListener("holdEnd", () =>
        this.stopSpinController()
      );
    }
  }
}

/**
 * Makes keyboard controls consistent with mouse controls for MAP camera mode
 */
export class MapKeyboardHoldControllerStrategy extends KeyboardHoldControllerStrategy {
  moveUp(delta: number) {
    const controls = this.registered?.controls;
    if (!controls) {
      return;
    }

    controls.forward(-delta, false);
  }
}
